import { useEffect, useState } from 'react';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Button, Card, Input, Loading } from '../../components/Ui';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';

/**
 * Where KAAMWALA sends the money.
 *
 * Customers pay KAAMWALA and KAAMWALA passes it on, so until this is filled
 * in there is nowhere for a provider's earnings to go. The account number is
 * typed twice on purpose - one wrong digit pays a stranger, and no amount of
 * support gets it back.
 *
 * It comes back masked even to its owner: these screens are often looked at
 * over somebody's shoulder.
 */
const Row = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: `1px solid ${colors.line}` }}>
    <div className="t-tiny" style={{ color: colors.muted }}>{label}</div>
    <div className="t-body" style={{ fontWeight: 700 }}>{value || '—'}</div>
  </div>
);

const BankDetailsPage = () => {
  const toast = useToast();
  const [saved, setSaved] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    holderName: '', accountNumber: '', confirmAccountNumber: '', ifsc: '', bankName: '', branch: '', upiId: '',
  });

  useEffect(() => {
    api.partners.bank()
      .then((r) => {
        setSaved(r.data);
        if (!r.data.onFile) setEditing(true);
        else {
          setForm((f) => ({
            ...f,
            holderName: r.data.holderName || '',
            ifsc: r.data.ifsc || '',
            bankName: r.data.bankName || '',
            branch: r.data.branch || '',
            upiId: r.data.upiId || '',
          }));
        }
      })
      .catch(() => { setSaved({ onFile: false }); setEditing(true); });
  }, []);

  const save = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const r = await api.partners.saveBank(form);
      setSaved(r.data);
      setEditing(false);
      setForm((f) => ({ ...f, accountNumber: '', confirmAccountNumber: '' }));
      toast.success(r.message);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!saved) return <Loading />;

  return (
    <div>
      <PageHeader title="Bank details" subtitle="Where your settlements are paid" />

      <Card style={{ padding: 16, marginBottom: 16, background: colors.primaryLight }}>
        <div className="t-small">
          KAAMWALA collects what customers pay and sends your share to this account.
        </div>
      </Card>

      {saved.onFile && !editing ? (
        <Card style={{ padding: 18 }}>
          <Row label="Name on the account" value={saved.holderName} />
          <Row label="Account number" value={saved.accountNumber} />
          <Row label="IFSC" value={saved.ifsc} />
          {saved.bankName ? <Row label="Bank" value={[saved.bankName, saved.branch].filter(Boolean).join(', ')} /> : null}
          {saved.upiId ? <Row label="UPI" value={saved.upiId} /> : null}
          <div
            style={{
              margin: '16px 0',
              padding: 12,
              borderRadius: 12,
              background: saved.verified ? colors.successBg : colors.warningBg,
              color: saved.verified ? colors.success : colors.warning,
              fontWeight: 700,
              fontSize: 13.5,
            }}
          >
            {saved.verified
              ? '✓ Verified — settlements will come here'
              : '⏳ KAAMWALA will check this account before the next settlement'}
          </div>
          <Button title="Change these details" variant="light" onClick={() => setEditing(true)} />
        </Card>
      ) : (
        <Card style={{ padding: 18 }}>
          <form onSubmit={save}>
            <Input
              label="Name on the account"
              value={form.holderName}
              onChange={(e) => setForm({ ...form, holderName: e.target.value })}
              placeholder="Exactly as the bank has it"
            />
            <Input
              label="Account number"
              value={form.accountNumber}
              onChange={(e) => setForm({ ...form, accountNumber: e.target.value.replace(/\D/g, '') })}
              inputMode="numeric"
              placeholder="6 to 18 digits"
            />
            <Input
              label="Account number again"
              value={form.confirmAccountNumber}
              onChange={(e) => setForm({ ...form, confirmAccountNumber: e.target.value.replace(/\D/g, '') })}
              inputMode="numeric"
              placeholder="Type it a second time"
              hint="One wrong digit pays somebody else, and it cannot be taken back."
            />
            <Input
              label="IFSC"
              value={form.ifsc}
              onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase().replace(/\s/g, '') })}
              placeholder="Like HDFC0001234"
            />
            <Input label="Bank (optional)" value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} />
            <Input label="Branch (optional)" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} />
            <Input
              label="UPI id (optional)"
              value={form.upiId}
              onChange={(e) => setForm({ ...form, upiId: e.target.value.trim() })}
              placeholder="name@bank"
              hint="Small settlements can go here instead, which is quicker."
            />
            <div className="flex" style={{ gap: 8, marginTop: 10 }}>
              <Button title={busy ? 'Saving…' : 'Save bank details'} loading={busy} onClick={save} />
              {saved.onFile ? (
                <Button title="Cancel" variant="light" onClick={() => setEditing(false)} />
              ) : null}
            </div>
          </form>
        </Card>
      )}
    </div>
  );
};

export default BankDetailsPage;
